import React, { useState, useEffect } from "react";
import {
  QueueListIcon,
  PhoneIcon,
  ClockIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { fetchPhoneroQueues } from "../../../api/company";
import RoleOnly from "../../../routes/roleOnly";

const QueuesList = ({ companyId }) => {
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (companyId) {
      loadQueues();
    }
  }, [companyId]);

  const loadQueues = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchPhoneroQueues();
      console.log("Phonero Queues API response:", response.data);

      if (response.data && response.data.success) {
        const queuesData = response.data.queues;
        setQueues(queuesData?.result || []);
      } else {
        setQueues([]);
      }
    } catch (err) {
      setError("Failed to load queues");
      console.error("Error loading queues:", err);
    } finally {
      setLoading(false);
    }
  };

  const getQueueStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <PhoneIcon className="h-4 w-4 text-green-600" />;
      case "inactive":
        return <PhoneIcon className="h-4 w-4 text-red-600" />;
      default:
        return <PhoneIcon className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getQueueStatusText = (status) => {
    switch (status) {
      case "active":
        return "Active";
      case "inactive":
        return "Inactive";
      default:
        return "Unknown";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadQueues}
          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (queues?.length === 0) {
    return (
      <div className="text-center py-8">
        <QueueListIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No queues</h3>
        <p className="mt-1 text-sm text-gray-500">
          No queues have been configured yet.
        </p>
      </div>
    );
  }

  return (
    <RoleOnly role="phone" action="read">
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Call Queues
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Active queues and their current status
          </p>
        </div>

        <ul className="divide-y divide-gray-200">
          {queues?.map((queue) => (
            <li key={queue.queueId} className="px-4 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {getQueueStatusIcon(queue.status || "active")}
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <p className="text-sm font-medium text-gray-900">
                        {queue.description || `Queue ${queue.queueId}`}
                      </p>
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {getQueueStatusText(queue.status || "active")}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center text-sm text-gray-500">
                      <QueueListIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                      Queue ID: {queue.queueId}
                    </div>
                    {queue.phoneNumber && (
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <PhoneIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                        {queue.phoneNumber}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center text-sm text-gray-500">
                  <span className="text-xs">ID: {queue.queueId}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>

        <div className="px-4 py-3 border-t border-gray-200">
          <button
            onClick={loadQueues}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Refresh
          </button>
        </div>
      </div>
    </RoleOnly>
  );
};

export default QueuesList;
